import moment from 'moment'
import { TSelectOpt } from 'transfix-ui/components/Select/SelectTypes'
import { Maybe, IRescheduleReasons, IRolledRescheduleReasons } from 'types/graphqlTypes'
import { RescheduleReasonLabels, RescheduleReasonsDisplayOrder } from 'types/rescheduleReasonsEnum'
import { titleCase } from 'utils/titleCase'
import FORMATS from 'constants/datetime'

export const getDateTime = (currentDate: string, currentTime: string, timezone: string) => {
  if (!currentDate || !currentTime || !timezone) {
    return ''
  }
  const currentMomentTime = moment(currentTime, 'HH:mm')
  return moment
    .tz(currentDate, timezone)
    .set({
      hour: currentMomentTime.hour(),
      minute: currentMomentTime.minutes(),
    })
    .format(FORMATS.iso8601FormatTimezone)
}

export const getIsIdealAppointmentTime = (
  suggestedDateTime: string | null,
  currentDateTime: string | null,
  timezone: string | null,
  isSuggestedTimesEnabled: boolean
): boolean => {
  if (!suggestedDateTime || !currentDateTime || !timezone) {
    return false
  }
  const currentDateMoment = moment.tz(currentDateTime, timezone)
  const suggestedDateMoment = moment.tz(suggestedDateTime, timezone)
  const duration = moment.duration(currentDateMoment.diff(suggestedDateMoment))
  const hours = duration.asHours()
  // If isSuggestedTimesEnabled don't use 2 hour range to determine if it's ideal appointment time, do an exact hour match
  return isSuggestedTimesEnabled ? Math.abs(hours) === 0 : Math.abs(hours) <= 2
}

export const getInputName = (stopIndex: number, name: string): string =>
  `shipmentStops.${stopIndex}.${name}`

export const RESCHEDULE_REASON_OPTIONS_WITH_SUGGESTED_TIMES: TSelectOpt[] = Object.entries(
  IRescheduleReasons
)
  .sort(
    ([a], [b]) =>
      RescheduleReasonsDisplayOrder.indexOf(a) - RescheduleReasonsDisplayOrder.indexOf(b)
  )
  .map(([key, value]) => ({
    value: key,
    label: RescheduleReasonLabels[value] || key,
  }))

export const RESCHEDULE_REASON_OPTIONS: TSelectOpt[] = RESCHEDULE_REASON_OPTIONS_WITH_SUGGESTED_TIMES.filter(
  item => item.value !== IRescheduleReasons.suggested_appointment
)

export const ROLLED_RESCHEDULE_REASONS: TSelectOpt[] = Object.entries(IRolledRescheduleReasons).map(
  ([key, value]) => ({
    value: key,
    label: RescheduleReasonLabels[value] || key,
  })
)

/**
 * Returns formatted label with fallbacks
 * @example getOptionLabel('8117 facility', 'GOSHEN', 'NY') // '8117 Facility, Goshen, NY'
 */
export const getOptionLabel = (
  name: Maybe<string>,
  city: Maybe<string>,
  stateCode: Maybe<string>
): string => {
  const cityFormatted = titleCase(city ?? '')
  const stateFormatted = stateCode ?? ''
  const location = [cityFormatted, stateFormatted].join(', ')
  return [name, location].join(' - ')
}

export const getTimeWithHour = (date: string, time: string) => {
  if (!(date && time)) return null

  const mDate = moment(date)
  const mTime = moment(time, FORMATS.militaryTimeFormat)

  if (!(mDate.isValid() && mTime.isValid())) return null

  const format = `${mDate.format(FORMATS.dateFormat)} ${mTime.format(FORMATS.militaryTimeFormat)}`
  return moment(format, FORMATS.datetimeInputFormat)
}

export const isSameDate = (startAtDate: string, endAtDate: string): boolean => {
  const startAt = moment(startAtDate)
  const endAt = moment(endAtDate)

  if (moment(startAt).isValid() && endAt.isValid()) {
    return endAt.isSame(startAt, 'date')
  }

  return false
}

export const allowEndAtToBeEmpty = (
  endAtTime: string,
  endAtDate: string,
  startAtDate: string,
  startAtTime: string
) => {
  return isSameDate(startAtDate, endAtDate) && !startAtTime && !endAtTime
}

export const validateRangeDates = (
  endAtTime: string,
  endAtDate: string,
  startAtDate: string,
  startAtTime: string
): boolean => {
  // if start and end dates are the same, only allow end time to be empty if start time is also empty
  if (allowEndAtToBeEmpty(endAtTime, endAtDate, startAtDate, startAtTime)) return true

  const startAt = getTimeWithHour(startAtDate, startAtTime)
  const endAt = getTimeWithHour(endAtDate, endAtTime)

  // if startAtDate is before endAtDate, then pass validation
  return Boolean(endAt) && Boolean(startAt) && moment(endAt).isAfter(startAt)
}
